import { PaymentModule } from './features/sale/payment/payment.module'
import { PowerBiReportRoutingModule } from './features/powerbi-report/powerbi-report-routing.module'
import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'

import { AuthGuard } from './core/auth/auth.guard'
import { BackendLayoutComponent } from './layout/backend/layout.component'
import { PageLayoutComponent } from './layout/page/page.component'
import { ModuleLevelGuard } from './shared/guards/module-level.guard'
import { environment } from '../environments/environment'
import { RestrictedToRoleGuard } from './features/role/user-role/restricted-to-role.guard'
import { RolesType } from './features/role/role'
import { UserAccessRightsGuard } from './features/role/user-role/user-access-rights.guard'
import { AccessRights } from './features/role/user-role/user-role'

const routes: Routes = [
  {
    path: 'oidc',
    loadChildren: () =>
      import('./core/auth/oidc/oidc.module').then((m) => m.OidcModule)
  },
  {
    path: 'dashboard',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, ModuleLevelGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.dashboardModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      )
  },
  {
    path: 'product-groups',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.productModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/product/product-group/product-group.module').then(
        (m) => m.ProductGroupModule
      )
  },
  {
    path: 'products',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.productModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/product/product.module').then((m) => m.ProductModule)
  },
  {
    path: 'locations',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.locationModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/location/location.module').then(
        (m) => m.LocationModule
      )
  },
  {
    path: 'translations',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/translation/translation.module').then(
        (m) => m.TranslationModule
      )
  },
  {
    path: 'email-templates',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.emailTemplateModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/email-template/email-template.module').then(
        (m) => m.EmailTemplateModule
      )
  },
  {
    path: 'tags',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/tag/tag.module').then((m) => m.TagModule)
  },
  {
    path: 'members',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.memberModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/member/member.module').then((m) => m.MemberModule)
  },
  {
    path: 'pages',
    component: PageLayoutComponent,
    loadChildren: () =>
      import('./features/pages/pages.module').then((m) => m.PagesModule)
  },
  {
    path: 'bookings',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.bookingModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/booking/booking.module').then((m) => m.BookingModule)
  },
  {
    path: 'participations',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.bookingModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/booking/participation/participation.module').then(
        (m) => m.ParticipationModule
      )
  },
  {
    path: 'organizations',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.organizationModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/organization/organization.module').then(
        (m) => m.OrganizationModule
      )
  },
  {
    path: 'user-roles',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/role/user-role/user-role.module').then(
        (m) => m.UserRoleModule
      )
  },
  {
    path: 'configuration-options',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard, UserAccessRightsGuard],
    data: {
      roles: environment.moduleConfigs.configOptionModule.restrictToRoles,
      accessRights: [AccessRights.Activities_write]
    },
    loadChildren: () =>
      import(
        './features/configuration-option/configuration-option.module'
      ).then((m) => m.ConfigurationOptionModule)
  },
  {
    path: 'operation-internal-events',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import(
        './features/operation/operation-internal-event/operation-internal-event.module'
      ).then((m) => m.OperationInternalEventModule)
  },
  {
    path: 'surveys',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.surveyModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/survey/survey.module').then((m) => m.SurveyModule)
  },
  {
    path: 'surveys-parents',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.surveyParentModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/survey-parent/survey-parent.module').then(
        (m) => m.SurveyParentModule
      )
  },
  {
    path: 'subscriptions',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.subscriptionModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/subscription/subscription.module').then(
        (m) => m.SubscriptionModule
      )
  },
  {
    path: 'courses',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.courseModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/course/course.module').then((m) => m.CourseModule)
  },
  {
    path: 'reports',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.reportModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/report/report.module').then((m) => m.ReportModule)
  },
  {
    path: 'powerbi-reports',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.powerBiReportModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/powerbi-report/powerbi-report.module').then(
        (m) => m.PowerBiReportModule
      )
  },
  {
    path: 'sales',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.saleModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/sale/sale.module').then((m) => m.SaleModule)
  },
  {
    path: 'payments',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.saleModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/sale/payment/payment.module').then(
        (m) => m.PaymentModule
      )
  },
  {
    path: 'supervisor-skills',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/supervisor-skills/supervisor-skills.module').then(
        (m) => m.SupervisorSkillsModule
      )
  },
  {
    path: 'gift-cards',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.giftCardModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/gift-card/gift-card.module').then(
        (m) => m.GiftCardModule
      )
  },
  {
    path: 'fine-rules',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.fineModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/fine-rule/fine-rule.module').then(
        (m) => m.FineRuleModule
      )
  },
  {
    path: 'employee-contracts',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/employee-contract/employee-contract.module').then(
        (m) => m.EmployeeContractModule
      )
  },
  {
    path: 'employee-bookings',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/employee-booking/employee-booking.module').then(
        (m) => m.EmployeeBookingModule
      )
  },
  {
    path: 'visits',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/visit/visit.module').then((m) => m.VisitModule)
  },
  {
    path: 'activities',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/activity/activity.module').then(
        (m) => m.ActivityModule
      )
  },
  {
    path: 'raffles',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.raffleModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/raffle/raffle.module').then((m) => m.RaffleModule)
  },
  {
    path: 'email-logs',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: [RolesType.ADMIN]
    },
    loadChildren: () =>
      import('./features/email-log/email-log.module').then(
        (m) => m.EmailLogModule
      )
  },
  {
    path: 'role-permissions',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.rolePermissionsModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/role-permissions/role-permissions.module').then(
        (m) => m.RolePermissionsModule
      )
  },
  {
    path: 'rentals',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.rentalModule.restrictToRoles
    },
    loadChildren: () =>
      import('./features/rental/rental.module').then((m) => m.RentalModule)
  },
  {
    path: 'visits',
    component: BackendLayoutComponent,
    canActivate: [AuthGuard, RestrictedToRoleGuard],
    data: {
      roles: environment.moduleConfigs.visitModule.isActive
    },
    loadChildren: () =>
      import('./features/visit/visit.module').then((m) => m.VisitModule)
  },
  // {
  //   path: 'serie-proposals',
  //   component: BackendLayoutComponent,
  //   canActivate: [AuthGuard, RestrictedToRoleGuard],
  //   data: {
  //     roles: environment.moduleConfigs.serieProposalModule.restrictToRoles
  //   },
  //   loadChildren: () =>
  //     import('./features/serie-proposal/serie-proposal.module').then(
  //       (m) => m.SerieProposalModule
  //     )
  // },
  {
    path: '',
    redirectTo: environment.moduleConfigs.authModule.afterLoginSuccessRedirect,
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: '/pages/error'
  }
]

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
    paramsInheritanceStrategy: 'always',
    onSameUrlNavigation: 'reload',
    scrollPositionRestoration: 'enabled'
})
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
